import { useCallback } from 'react';
import { atom, selectorFamily, useRecoilState, useSetRecoilState, DefaultValue } from 'recoil';

import { useFieldMaster } from '../../../hooks/useFieldMaster';
import { components } from '../../../schema/data-crud';

/*
 * Types
 */
export type SearchCondition = {
  id: string; // Reactのkeyに使うためのID
  categoryName: string;
  fieldId: string;
  operator: '' | 'eq' | 'ge' | 'le' | 'contain' | 'startsWith' | 'between';
  value: string[];
  isValid: boolean;
  errorMessage: string;
};

function checkCondition(condition: SearchCondition, fieldType: components['schemas']['FieldType']): [boolean, string] {
  // 両方検証可能な値が設定されている場合のみ、範囲の妥当性をチェック。
  const checkNumericRange = (from: string, to: string): [boolean, string] => {
    const nf = Number(from);
    const nt = Number(to);
    const ok = isNaN(nf) || isNaN(nt) || nf <= nt;
    return ok ? [ok, ''] : [ok, '範囲が無効です'];
  }

  const checkDateRange = (from: string, to: string): [boolean, string] => {
    const df = Date.parse(from);
    const dt = Date.parse(to);
    const ok = isNaN(df) || isNaN(dt) || df <= dt;
    return ok ? [ok, ''] : [ok, '範囲が無効です'];
  }

  switch (condition.operator) {
    case 'between':
      switch (fieldType) {
        case 'INT':
        case 'FLOAT':
          return checkNumericRange(condition.value[0], condition.value[1]);
        case 'DATE':
        case 'DATETIME':
          return checkDateRange(condition.value[0], condition.value[1]);
      }
  }
  return [true, ''];
}

/*
 * States
 */

export const searchConditionListState = atom<SearchCondition[]>({
  key: 'searchConditionListState',
  default: [
    {
      id: 'initialId',
      categoryName: '',
      fieldId: '',
      operator: '',
      value: [''],
      isValid: true,
      errorMessage: '',
    },
  ],
});

export const searchConditionState = selectorFamily<SearchCondition | undefined, { id: string }>({
  key: 'searchConditionState',
  get:
    ({ id }) =>
    ({ get }) => {
      const searchConditionList = get(searchConditionListState);
      return searchConditionList.find((item) => item.id === id);
    },
  set:
    ({ id }) =>
    ({ set, get }, newValue) => {
      const searchConditionList = get(searchConditionListState);
      const newSearchConditionList = searchConditionList.map((item) => {
        if (item.id === id && newValue !== undefined && !(newValue instanceof DefaultValue)) {
          return newValue;
        }
        return item;
      });
      set(searchConditionListState, newSearchConditionList);
    },
});

/*
 * Hook
 */
export const useSearchCondition = (id: string) => {
  const [searchCondition, setSearchCondition] = useRecoilState(searchConditionState({ id }));
  const setSearchConditionList = useSetRecoilState(searchConditionListState);
  const fieldMaster = useFieldMaster();

  const selectedCategoriesfields = [
    ...fieldMaster.dispatchInfoCategories,
    ...fieldMaster.patientInfoCategories,
  ]
    .filter(({ categoryName }) => categoryName === searchCondition?.categoryName)
    .flatMap((category) => category.fields);

  const selectedField = selectedCategoriesfields?.find(
    (item) => item.fieldId === searchCondition?.fieldId
  );

  // カテゴリー変更
  const setCategory = useCallback(
    (categoryName: string) => {
      setSearchCondition((prev) =>
        prev !== undefined
          ? { ...prev, categoryName, fieldId: '', operator: '', value: [''], isValid: true, errorMessage: '' }
          : undefined
      );
    },
    [setSearchCondition]
  );

  // フィールド変更
  const setField = useCallback(
    (fieldId: string) => {
      setSearchCondition((prev) => {
        if (prev === undefined) {
          return undefined;
        }
        return {
          ...prev,
          fieldId,
          operator: '',
          value: [''],
          isValid: true,
          errorMessage: '',
        };
      });
    },
    [setSearchCondition]
  );

  // オペレータ変更
  const setOperator = useCallback(
    (operator: string) => {
      switch (operator) {
        case 'eq':
        case 'ge':
        case 'le':
        case 'contain':
        case 'startsWith':
          setSearchCondition((prev) => {
            if (prev === undefined) {
              return undefined;
            }
            if (prev.operator === 'between') {
              return { ...prev, operator, value: [''], isValid: true, errorMessage: '' };
            }
            return { ...prev, operator, isValid: true, errorMessage: '' };
          });
          break;
        case 'between':
          setSearchCondition((prev) => {
            if (prev === undefined) {
              return undefined;
            }
            return { ...prev, operator, value: ['', ''], isValid: true, errorMessage: '' };
          });
          break;
        default:
          break;
      }
    },
    [setSearchCondition]
  );

  // 値の変更
  const setValue = useCallback(
    (value: string) => {
      setSearchCondition((prev) => {
        if (prev === undefined) {
          return undefined;
        }
        return { ...prev, value: [value] };
      });
    },
    [setSearchCondition]
  );

  const setValueFrom = useCallback(
    (value: string) => {
      setSearchCondition((prev) => {
        if (prev === undefined) {
          return undefined;
        }
        const newCondition = { ...prev, value: [value, prev.value[1]] };
        const [isValid, errorMessage] = checkCondition(newCondition, selectedField?.fieldType ?? 'STRING');
        newCondition.isValid = isValid;
        newCondition.errorMessage = errorMessage;
        return newCondition;
      });
    },
    [setSearchCondition, selectedField]
  );

  const setValueTo = useCallback(
    (value: string) => {
      setSearchCondition((prev) => {
        if (prev === undefined) {
          return undefined;
        }
        const newCondition = { ...prev, value: [prev.value[0], value] };
        const [isValid, errorMessage] = checkCondition(newCondition, selectedField?.fieldType ?? 'STRING');
        newCondition.isValid = isValid;
        newCondition.errorMessage = errorMessage;
        return newCondition;
      });
    },
    [setSearchCondition, selectedField]
  );

  const setMultipleValue = useCallback(
    (value: string[]) => {
      setSearchCondition((prev) => {
        if (prev === undefined) {
          return undefined;
        }
        return { ...prev, value };
      });
    },
    [setSearchCondition]
  );

  // 検索条件を削除
  const deleteCondition = useCallback(() => {
    setSearchConditionList((prev) => prev.filter((item) => item.id !== id));
  }, [id, setSearchConditionList]);

  return {
    searchCondition,
    selectedCategoriesfields,
    selectedField,
    setCategory,
    setField,
    setOperator,
    setValue,
    setValueFrom,
    setValueTo,
    setMultipleValue,
    deleteCondition,
  };
};
